import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatApiError } from '@/lib/formatApiError';
import {
  navigateRazorpayCheckoutTab,
  prepareRazorpayCheckoutTab,
  runRazorpayCheckout,
  stashCheckoutSession,
  subscribeRazorpayMessages,
  syncUserCredits,
} from '@/lib/billing/razorpayCheckout';

export { syncUserCredits };

export function paymentSuccessMessage(verified) {
  const plan = verified?.current_plan_name;
  const credits = verified?.credits_granted;
  if (verified?.already_processed) {
    return plan ? `Payment already credited. Current plan: ${plan}.` : 'Payment already credited';
  }
  if (plan) {
    return `You're on the ${plan} plan. Added ${credits} credits.`;
  }
  return `Added ${credits} credits to your wallet`;
}

export function useCreditRecharge({ onSuccess } = {}) {
  const [payingPlanId, setPayingPlanId] = useState(null);

  useEffect(() => {
    return subscribeRazorpayMessages({
      onSuccess: (verified) => {
        toast.success(paymentSuccessMessage(verified));
        if (typeof verified?.balance === 'number') {
          syncUserCredits(verified.balance, verified);
        }
        onSuccess?.(verified);
        setPayingPlanId(null);
      },
      onFailed: () => setPayingPlanId(null),
      onCancelled: () => setPayingPlanId(null),
    });
  }, [onSuccess]);

  const startRecharge = useCallback(async (amountInrOrPlan, planId = 'custom') => {
    const isPlanObject = typeof amountInrOrPlan === 'object' && amountInrOrPlan !== null;
    const plan_id = isPlanObject && !amountInrOrPlan.custom
      ? amountInrOrPlan.id
      : (planId !== 'custom-amount' && planId !== 'custom' ? planId : null);
    const amount = isPlanObject ? null : parseFloat(amountInrOrPlan);

    if (!plan_id && (!Number.isFinite(amount) || amount < 1)) {
      toast.error('Enter a valid amount (minimum ₹1)');
      return;
    }

    setPayingPlanId(plan_id || planId);

    const checkoutTab = prepareRazorpayCheckoutTab();

    try {
      const body = plan_id ? { plan_id } : { amount_inr: amount };
      const { data: order } = await api.post('/billing/recharge/create-order', body);

      if (checkoutTab && navigateRazorpayCheckoutTab(checkoutTab, order, { plan_id: plan_id || planId })) {
        toast.info('Complete payment in the Razorpay tab.');
        return;
      }

      if (checkoutTab && !checkoutTab.closed) {
        checkoutTab.close();
      }

      const session = stashCheckoutSession(order, { plan_id: plan_id || planId });
      toast.info('Opening Razorpay checkout…');
      const result = await runRazorpayCheckout(session);
      if (result?.ok) {
        toast.success(paymentSuccessMessage(result.verified));
        if (typeof result.verified?.balance === 'number') {
          syncUserCredits(result.verified.balance, result.verified);
        }
        onSuccess?.(result.verified);
      }
      setPayingPlanId(null);
    } catch (err) {
      if (checkoutTab && !checkoutTab.closed) {
        checkoutTab.close();
      }
      toast.error(formatApiError(err, 'Could not start payment'));
      setPayingPlanId(null);
    }
  }, [onSuccess]);

  return { startRecharge, payingPlanId, isPaying: Boolean(payingPlanId) };
}
